import React, { useState } from 'react';
import './EnrollmentModal.css';
import { motion, AnimatePresence } from 'framer-motion';

const EnrollmentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    seatType: '1. Confirm seat for admission',
    name: '',
    contact: '',
    college: '',
    year: 'FE',
    gender: 'Male',
    ref: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenderSelect = (genderValue) => {
    setFormData(prev => ({ ...prev, gender: genderValue }));
  };

  const handleSeatTypeSelect = (seatTypeValue) => {
    setFormData(prev => ({ ...prev, seatType: seatTypeValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const scriptURL = "https://script.google.com/macros/s/AKfycbybc55JPxbHW3GjXaotZEwi0wL5AZNRXCkYnZty5QnJUd0I172lVvbug2CfuwsTkLWh/exec";

      const now = new Date();

      const dateStr = now.toLocaleDateString("en-GB");

      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      });

      const fullDateTime = `${dateStr} ${timeStr}`;

      const refVal = formData.ref.trim() || "Direct";

      const seatVal = formData.seatType
        .toLowerCase()
        .includes("admission")
        ? "Admission"
        : "Demo";

      const payload = {
        date: fullDateTime,
        name: formData.name.trim(),
        contact: formData.contact.trim(),
        college: formData.college.trim(),
        year: formData.year,
        gender: formData.gender,
        ref: refVal,
        seatType: seatVal
      };

      await fetch(scriptURL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain"
        },
        body: JSON.stringify(payload)
      });

      setIsSuccess(true);

      setTimeout(() => {
        setIsSuccess(false);
        onClose();

        setFormData({
          seatType: "1. Confirm seat for admission",
          name: "",
          contact: "",
          college: "",
          year: "FE",
          gender: "Male",
          ref: ""
        });
      }, 2200);

    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonText = () => {
    if (isSubmitting) return "Processing...";
    if (formData.seatType.startsWith("1") || formData.seatType.toLowerCase().includes("admission")) {
      return "Confirm Seat For Admission";
    }
    return "Confirm Seat For Demo Lecture";
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="em-modal-overlay" onClick={onClose}>
          <motion.div
            className="em-modal-card"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="em-close-btn" onClick={onClose} aria-label="Close modal">
              &times;
            </button>

            {isSuccess ? (
              <div className="em-success-view">
                <div className="em-success-icon">✓</div>
                <h3>Seat Reserved Successfully!</h3>
                <p>Thank you for registering. Our team will contact you shortly.</p>
              </div>
            ) : (
              <>
                {/* Official Enrollment Badge */}
                <div className="em-badge">
                  <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 1H9L13 5V14C13 14.5523 12.5523 15 12 15H3C2.44772 15 2 14.5523 2 14V2C2 1.44772 2.44772 1 3 1Z" stroke="#3730A3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8.5 1V5.5H13" stroke="#3730A3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.5 8.5H9.5" stroke="#3730A3" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M4.5 11.5H7.5" stroke="#3730A3" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span>OFFICIAL ENROLLMENT</span>
                </div>

                <div className="em-header">
                  <h2>Java Full Stack Development Course</h2>
                  <p>Reserve your seat for Core2web's premium Full Stack program.</p>
                </div>

                <form className="em-form" onSubmit={handleSubmit}>
                  {/* Seat Type Selection */}
                  <div className="em-form-group full-width">
                    <label className="em-label">
                      Seat Type Selection <span className="em-required">*</span>
                    </label>
                    <div className="em-seat-selector">
                      <button
                        type="button"
                        className={`em-seat-option ${formData.seatType === '1. Confirm seat for admission' ? 'active' : ''
                          }`}
                        onClick={() => handleSeatTypeSelect('1. Confirm seat for admission')}
                      >
                        1. Confirm seat for admission
                      </button>
                      <button
                        type="button"
                        className={`em-seat-option ${formData.seatType === '2. Confirm seat for demo lecture' ? 'active' : ''
                          }`}
                        onClick={() => handleSeatTypeSelect('2. Confirm seat for demo lecture')}
                      >
                        2. Confirm seat for demo lecture
                      </button>
                    </div>
                  </div>

                  {/* Form Grid */}
                  <div className="em-grid">
                    {/* Full Name */}
                    <div className="em-form-group">
                      <label className="em-label">
                        Full Name <span className="em-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="em-input"
                        placeholder="e.g. Rahul Sharma"
                        required
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Contact Number */}
                    <div className="em-form-group">
                      <label className="em-label">
                        Contact Number <span className="em-required">*</span>
                      </label>
                      <input
                        type="tel"
                        name="contact"
                        className="em-input"
                        placeholder="10-digit mobile number"
                        required
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit mobile number"
                        value={formData.contact}
                        onChange={handleChange}
                      />
                    </div>

                    {/* College Name */}
                    <div className="em-form-group">
                      <label className="em-label">
                        College Name <span className="em-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="college"
                        className="em-input"
                        placeholder="e.g. COEP Pune / MIT World Peace"
                        required
                        value={formData.college}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Year of Study */}
                    <div className="em-form-group">
                      <label className="em-label">
                        Year of Study <span className="em-required">*</span>
                      </label>
                      <div className="em-select-wrapper">
                        <select
                          name="year"
                          className="em-select"
                          required
                          value={formData.year}
                          onChange={handleChange}
                        >
                          <option value="FE">FE</option>
                          <option value="SE">SE</option>
                          <option value="TE">TE</option>
                          <option value="BE">BE</option>
                          <option value="Passout">Passout</option>
                          <option value="Other">Other</option>
                        </select>
                        <span className="em-select-arrow">▾</span>
                      </div>
                    </div>

                    {/* Gender */}
                    <div className="em-form-group">
                      <label className="em-label">
                        Gender <span className="em-required">*</span>
                      </label>
                      <div className="em-gender-pills">
                        {['Male', 'Female', 'Other'].map(g => (
                          <button
                            key={g}
                            type="button"
                            className={`em-gender-pill ${formData.gender === g ? 'active' : ''}`}
                            onClick={() => handleGenderSelect(g)}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Reference */}
                    <div className="em-form-group">
                      <label className="em-label">
                        Reference (Referred By / Source)
                      </label>
                      <input
                        type="text"
                        name="ref"
                        className="em-input"
                        placeholder="e.g. Friend Name, Instagram, YouTube..."
                        value={formData.ref}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="em-submit-btn" disabled={isSubmitting}>
                    <span>{getButtonText()}</span>
                    {!isSubmitting && <span className="em-btn-arrow">&rarr;</span>}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EnrollmentModal;
